"use client";

import * as React from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const servicesData = [{
  title: "For Care Seekers",
  description: "Sync your calendar to seamlessly access care and get personalized health recommendations based on your schedule.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1630137/img/relaxing_phone.png"
}, {
  title: "Healthcare Partners",
  description: "Get your provider schedules online and streamline patient access with our intuitive, modern integration tools.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1630137/img/bright_clinic.png"
}, {
  title: "The Opportunity",
  description: "Explore why investing in simplified healthcare access and better scheduling is the future of medical care.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1630137/img/investor_path.png"
}, {
  title: "Our Mission",
  description: "Learn about our commitment to making healthcare access effortless, empathetic, and organized for everyone.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1630137/img/mission_team.png"
}, {
  title: "Smart Syncing",
  description: "Our advanced calendar integration ensures you never miss an appointment or a wellness check-up again.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1630137/img/calendar_integration.png"
}, {
  title: "Provider Portal",
  description: "A centralized dashboard for physicians to manage availability and connect with patients effortlessly.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1630137/img/provider_dashboard.png"
}, {
  title: "Growth Metrics",
  description: "Detailed analytics for our partners and investors to track the impact of easier scheduling on health outcomes.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1630137/img/growth_metrics.png"
}, {
  title: "Reach Out",
  description: "Have questions about our platform? Our team is here to help you navigate your journey to better health.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1630137/img/contact_support.png"
}];
export default function ExpertCareServices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section data-section-id="39238" id="choose-your-path" ref={ref} className={`py-24 bg-background transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {}
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-primary" />
            <span className="text-primary font-sans font-semibold tracking-wider uppercase text-sm">
              May it be easy for you
            </span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-6">
            Choose Your Path
          </h2>
          
          <div className="flex items-center justify-center gap-4 mb-6">
            <Separator className="w-12 bg-primary/30" />
            <div className="w-3 h-3 rounded-full bg-primary border-2 border-background" />
            <Separator className="w-12 bg-primary/30" />
          </div>

          <p className="text-muted-foreground font-sans text-lg leading-relaxed">
            Better access to care starts with easier scheduling. Explore how Kolay Health 
            simplifies the journey for patients, providers, and partners.
          </p>
        </div>

        {}
        <div className="relative px-4 md:px-12">
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full">
            <CarouselContent className="-ml-4 md:-ml-8">
              {servicesData.map((service, index) => <CarouselItem key={index} data-index={index} className="pl-4 md:pl-8 md:basis-1/2 lg:basis-1/3">
                  <div className="h-full py-4"> {}
                    <Card className="group relative h-full overflow-hidden border-none bg-card shadow-lg transition-all duration-500 hover:-translate-y-4 hover:shadow-2xl rounded-3xl">
                      {}
                      <div className="relative h-64 w-full overflow-hidden">
                        <img src={service.image} alt={service.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                        {}
                        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-card to-transparent opacity-100" />
                      </div>

                      {}
                      <CardContent className="relative z-10 -mt-6 px-8 pb-8 pt-0 text-center">
                        <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary shadow-sm group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                           <Sparkles className="h-6 w-6" />
                        </div>
                        
                        <h3 className="mb-3 font-default text-2xl font-bold text-primary transition-colors duration-300">
                          {service.title}
                        </h3>
                        
                        <p className="mb-6 text-muted-foreground font-sans leading-relaxed">
                          {service.description}
                        </p>

                        <Button variant="ghost" className="group/btn text-primary hover:text-primary hover:bg-primary/5 font-semibold" asChild>
                          <Link to="#">
                            Learn More
                            <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                          </Link>
                        </Button>
                      </CardContent>
                    </Card>
                  </div>
                </CarouselItem>)}
            </CarouselContent>
            
            {}
            <div className="hidden md:block">
              <CarouselPrevious className="absolute -left-4 top-1/2 -translate-y-1/2 h-12 w-12 border-primary/20 bg-background text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300" />
              <CarouselNext className="absolute -right-4 top-1/2 -translate-y-1/2 h-12 w-12 border-primary/20 bg-background text-primary hover:bg-primary hover:text-primary-foreground transition-all duration-300" />
            </div>
          </Carousel>
        </div>

        {}
        <div className="mt-12 text-center md:hidden">
          <Button size="lg" className="rounded-full px-8 bg-primary text-primary-foreground hover:bg-primary/90" asChild>
            <Link to="#">View All Services</Link>
          </Button>
        </div>
      </div>
    </section>;
}